// 案 E: Apple Health 風 (極端 minimal)
// - 「経営ヘルス: 良好 / 注意 / 警告」を最上位 1 行
// - 2×2 タイルだけ (入院収入 / 患者数 / 単価 / 在院日数 等の主要 4)
// - 「注目すべき診療科」を底に 2-3 件
// - その下にトレンド 1 個 (入院収入のみ)

import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta, ml, ALERT_CATEGORIES } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { Sparkline } from "../components/Sparkline";
import { fmtByUnitMain, fmtByUnit } from "../../lib/format";
import type { KpiResponse, AlertsResponse, AlertItem, MetricRow, Direction } from "@shared/api";

const TILE_METRICS = ["入院収入", "入院患者延数", "入院単価", "平均在院日数"];

export function PrototypeE() {
  const { data: meta } = useMeta();
  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;
  const asOfMonth = meta.dateMax;
  const periodFrom = monthShift(asOfMonth, -11);
  const ymLabel = `${asOfMonth.slice(0, 4)}年${asOfMonth.slice(5, 7)}月`;

  return (
    <MobileShell title={`経営ヘルス · ${ymLabel}`} prototypeId="E" prototypeName="Apple Health 風" asOfMonth={asOfMonth}>
      <HealthSummary asOfMonth={asOfMonth} metrics={meta.metrics} />
      <div className="grid grid-cols-2 gap-2">
        {TILE_METRICS.map((m) => (
          <Tile key={m} metricName={m} metrics={meta.metrics} asOfMonth={asOfMonth} periodFrom={periodFrom} />
        ))}
      </div>
      <NotableDepts asOfMonth={asOfMonth} />
      <RevenueTrend asOfMonth={asOfMonth} periodFrom={periodFrom} />
      <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
        詳しい分析は PC 版で。 ここは「朝起きて 10 秒で見る」ためのページ。
      </p>
    </MobileShell>
  );
}

// 「経営ヘルス: 良好 / 注意 / 警告」総合判定
function HealthSummary({ asOfMonth, metrics }: { asOfMonth: string; metrics: MetricRow[] }) {
  const periodFrom = monthShift(asOfMonth, -11);
  const queries = TILE_METRICS.map((m) =>
    useQuery<KpiResponse>({
      queryKey: ["kpi-e-summary", m, asOfMonth],
      queryFn: () => api.kpi({
        granularity: "小", iryo: "合計", yotei: "合計", metric: m,
        asOfMonth, periodFrom,
      }),
      placeholderData: (prev) => prev,
    })
  );

  // 達成 (ratio>=1.0) / 未達 (<0.95) で総合判定
  const tally = queries.reduce((acc, q, i) => {
    const data = q.data;
    if (!data || data.current == null || data.budget == null || data.budget === 0) return acc;
    const metricDef = metrics.find((x) => x["集計区分"] === TILE_METRICS[i]);
    const direction = (metricDef?.direction ?? "neutral") as Direction;
    const reverse = direction === "lower_is_better";
    const v = reverse ? 2 - data.current / data.budget : data.current / data.budget;
    if (v >= 1.0) acc.ok++;
    else if (v >= 0.95) acc.warn++;
    else acc.danger++;
    return acc;
  }, { ok: 0, warn: 0, danger: 0 });

  const overall = tally.danger > 0 ? "warn" : tally.warn > 0 ? "caution" : "ok";
  const config = {
    ok:      { label: "良好",  emoji: "✓", color: "from-emerald-100 to-emerald-50", text: "text-emerald-900", subtitle: "主要指標すべて予算達成中" },
    caution: { label: "注意",  emoji: "⚠", color: "from-amber-100 to-amber-50",   text: "text-amber-900",   subtitle: `${tally.warn} 指標が予算未達 (-5% 以内)` },
    warn:    { label: "警告",  emoji: "✕", color: "from-red-100 to-red-50",      text: "text-red-900",      subtitle: `${tally.danger} 指標が大幅未達 (-5% 超)` },
  }[overall];

  return (
    <section className={`bg-gradient-to-br ${config.color} rounded-2xl p-4 ${config.text} shadow-sm`}>
      <div className="flex items-center gap-2">
        <span className="text-3xl">{config.emoji}</span>
        <div>
          <div className="text-2xl font-bold tracking-tight leading-none">{config.label}</div>
          <div className="text-[11px] mt-1 opacity-80">{config.subtitle}</div>
        </div>
      </div>
      <div className="mt-3 flex gap-2 text-[10px]">
        <span className="px-2 py-0.5 rounded bg-white/60">達成 {tally.ok}</span>
        <span className="px-2 py-0.5 rounded bg-white/60">注意 {tally.warn}</span>
        <span className="px-2 py-0.5 rounded bg-white/60">警告 {tally.danger}</span>
      </div>
    </section>
  );
}

function Tile({ metricName, metrics, asOfMonth, periodFrom }: {
  metricName: string; metrics: MetricRow[]; asOfMonth: string; periodFrom: string;
}) {
  const metricDef = metrics.find((x) => x["集計区分"] === metricName);
  const unit = metricDef?.unit ?? "";
  const direction = (metricDef?.direction ?? "neutral") as Direction;
  const { data } = useQuery<KpiResponse>({
    queryKey: ["kpi-e-tile", metricName, asOfMonth],
    queryFn: () => api.kpi({
      granularity: "小", iryo: "合計", yotei: "合計", metric: metricName, asOfMonth, periodFrom,
    }),
    placeholderData: (prev) => prev,
  });
  const adj = (v: number | null | undefined): number | null => {
    if (v == null) return null;
    if (["GP", "GP単価"].includes(metricName) && v === 0) return null;
    return v;
  };
  const current = adj(data?.current);
  const main = fmtByUnitMain(current, unit);
  const ratio = data?.budget != null && current != null && data.budget !== 0 ? current / data.budget : null;
  const reverse = direction === "lower_is_better";
  const v = ratio == null ? null : reverse ? 2 - ratio : ratio;
  const dotColor = v == null ? "bg-slate-200"
                 : v >= 1.0 ? "bg-emerald-500"
                 : v >= 0.95 ? "bg-amber-500"
                              : "bg-red-500";

  const trendValues = data?.trend.map((p) => p.actual) ?? [];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <div className="text-[11px] text-slate-600">{ml(metricName)}</div>
      </div>
      <div className="mt-1 flex items-baseline gap-0.5">
        <span className="text-2xl font-bold text-slate-900 tabular-nums tracking-tight">{main.value}</span>
        <span className="text-[10px] text-slate-400">{main.unit}</span>
      </div>
      {trendValues.length > 1 && (
        <div className="mt-1 -mx-1">
          <Sparkline values={trendValues} width={140} height={24} showLast stroke="#0f172a" fill="rgba(15,23,42,0.06)" />
        </div>
      )}
      <div className="text-[10px] text-slate-500 mt-1">予算 {fmtByUnit(data?.budget, unit)}</div>
    </div>
  );
}

function NotableDepts({ asOfMonth }: { asOfMonth: string }) {
  const { data } = useQuery<AlertsResponse>({
    queryKey: ["alerts-e", asOfMonth],
    queryFn: () => api.alerts({ granularity: "小", iryo: "合計", yotei: "合計", asOfMonth }),
    placeholderData: (prev) => prev,
  });
  // 最も悪化している 3 科 (予算未達のトップ)
  const top: AlertItem[] = (data?.budgetUnder ?? []).slice(0, 3);
  if (top.length === 0) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
      <h2 className="text-xs text-slate-500 font-medium mb-2">⚠ 注目すべき診療科</h2>
      <div className="space-y-1.5">
        {top.map((it) => (
          <div key={it["診療科"]} className="flex items-center justify-between text-sm">
            <span className="text-slate-800">{it["診療科"]}</span>
            <span className="text-xs text-slate-500">{ALERT_CATEGORIES[0].label} <b className="text-red-700 ml-1">{it.ratio != null ? `${(it.ratio * 100).toFixed(0)}%` : "—"}</b></span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RevenueTrend({ asOfMonth, periodFrom }: { asOfMonth: string; periodFrom: string }) {
  const { data } = useQuery<KpiResponse>({
    queryKey: ["kpi-e-trend", asOfMonth],
    queryFn: () => api.kpi({
      granularity: "小", iryo: "合計", yotei: "合計", metric: "入院収入", asOfMonth, periodFrom,
    }),
    placeholderData: (prev) => prev,
  });
  const trend = data?.trend ?? [];
  if (trend.length < 2) return null;
  const actualValues = trend.map((p) => p.actual);
  const budgetValues = trend.map((p) => p.budget);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
      <h2 className="text-xs text-slate-500 font-medium mb-1">入院収入 24ヶ月推移</h2>
      <div className="-mx-1">
        <Sparkline values={actualValues} width={340} height={48} showLast stroke="#0f172a" fill="rgba(15,23,42,0.08)" />
      </div>
      <div className="-mx-1 -mt-12 opacity-50">
        <Sparkline values={budgetValues} width={340} height={48} stroke="#94a3b8" fill="transparent" />
      </div>
      <div className="mt-1 text-[10px] text-slate-500 flex gap-3">
        <span><span className="inline-block w-2 h-2 rounded-full bg-slate-900 mr-1" />実績</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-slate-400 mr-1" />予算</span>
      </div>
    </section>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
